import {
  SET_SCAN_PACK_CURRENT_ORDER,
  SET_SCAN_PACK_BARCODE_INPUT,
  SET_SCAN_PACK_MAIN_INPUT_FOCUS,
  SET_SCAN_PACK_CURRENT_ITEM,
  SET_SCAN_PACK_PROMPT,
  SET_SCAN_PACK_SCAN_SUCCESS,
  SET_SCAN_PACK_SCAN_ERROR,
  SET_TYPE_SCAN_STATE,
  SET_TYPE_SCAN_ADJACENT_SCANS,
  ADD_SCAN_PACK_LOCAL_LOG,
  SET_ACTIVE_BOX_INDEX,
  SET_MULTI_BOX_ALERT,
  SET_PRINT_SLIP_NOTICE,
  CLEAR_PRINT_SLIP_NOTICE,
  SET_SCAN_PACK_LOADING,
  SET_ORDER_COMPLETE,
  SET_ORDER_COMPLETE_ACTION,
  SET_POST_SCAN_PIPELINE,
  SET_POST_SCAN_TRACKING_INPUT,
  SET_ALIAS_STATE,
  SET_SHIPMENT_STATE,
  SET_NAVIGATION_SOURCE,
  SET_WMS_ZERO_PENDING,
  SET_BOX_CHAMP_STATE,
  RESET_BOX_CHAMP_STATE
} from "../constants";
import {
  initialPostScanPipeline,
  initialBoxChampState,
  initialBoxChampOrderState
} from './scanpackScanningInitialState';

const initialState = {
  currentOrder: null,
  currentItem: null,
  barcodeValue: '',
  mainInputHasFocus: true,
  scanSuccess: false,
  scanError: false,
  typeScanState: '',
  typeScanAdjacentScans: 0,
  localLogs: [],
  activeBoxIndex: 0,
  multiBoxAlert: false,
  emptyBoxPrint: false,
  printSlipNotice: null,
  loading: false,
  orderComplete: false,
  orderCompleteAction: false,
  alias: {
    addBarcodeWindow: false,
    addBarcodeItem: null,
    addBarcodeText: '',
    aliasVisible: false,
    shareBarcodeShow: false,
    productData: null
  },
  shipment: {
    visible: false,
    data: null,
    errorMessage: '',
    errorMessageShow: false
  },
  navigationSource: { from: null, routeParams: null },
  postScanPipeline: { ...initialPostScanPipeline },
  prompt: {
    isVisible: false,
    message: '',
    type: '',
    input: '',
    error: false
  },
  // Carries the Z3 stop-and-receive ask's state ACROSS the chained escalation/lot/quantity
  // prompts, since `prompt` itself only holds `message`/`type`/`input`/`error` (see
  // ScanPackPrompts.js). `null` when no Z3 gate is in progress. MUST be cleared on both prompt
  // cancel and successful commit (see clearWmsZeroPending in scanpackScanningSetters.js) — a
  // stale escalation code leaking into a later, unrelated scan would be a real security problem.
  wmsZeroPending: null,
  boxChamp: { ...initialBoxChampState }
};

export default function scanPackScanningReducer(state = initialState, action) {
  switch (action.type) {
    case SET_SCAN_PACK_CURRENT_ORDER: {
      const isNewOrder = !action.payload || action.payload?.id !== state.currentOrder?.id;
      return {
        ...state,
        currentOrder: action.payload,
        ...(isNewOrder && {
          localLogs: [],
          activeBoxIndex: 0,
          loading: false,
          orderComplete: false,
          orderCompleteAction: false,
          postScanPipeline: { ...initialPostScanPipeline },
          shipment: { ...initialState.shipment },
          boxChamp: { ...state.boxChamp, ...initialBoxChampOrderState }
        })
      };
    }
    case SET_SCAN_PACK_BARCODE_INPUT:
      return {
        ...state,
        barcodeValue: action.payload
      };
    case SET_SCAN_PACK_MAIN_INPUT_FOCUS:
      return {
        ...state,
        mainInputHasFocus: action.payload,
      };
    case SET_SCAN_PACK_CURRENT_ITEM:
      return {
        ...state,
        currentItem: action.payload
      };
    case SET_SCAN_PACK_PROMPT:
      return {
        ...state,
        prompt: action.payload
      };
    case SET_SCAN_PACK_SCAN_SUCCESS:
      return {
        ...state,
        scanSuccess: action.payload
      };
    case SET_SCAN_PACK_SCAN_ERROR:
      return {
        ...state,
        scanError: action.payload
      };
    case SET_TYPE_SCAN_STATE:
      return {
        ...state,
        typeScanState: action.payload
      };
    case SET_TYPE_SCAN_ADJACENT_SCANS:
      return {
        ...state,
        typeScanAdjacentScans: action.payload
      };
    case ADD_SCAN_PACK_LOCAL_LOG:
      return {
        ...state,
        localLogs: [action.payload, ...state.localLogs]
      };
    case SET_ACTIVE_BOX_INDEX:
      return {
        ...state,
        activeBoxIndex: action.payload
      };
    case SET_MULTI_BOX_ALERT:
      return {
        ...state,
        multiBoxAlert: action.payload,
        emptyBoxPrint: action.emptyBoxPrint || false
      };
    case SET_PRINT_SLIP_NOTICE:
      return {...state, printSlipNotice: action.payload};
    case CLEAR_PRINT_SLIP_NOTICE:
      return state.printSlipNotice === action.payload ? {...state, printSlipNotice: null} : state;
    case SET_SCAN_PACK_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    case SET_ORDER_COMPLETE:
      return {
        ...state,
        orderComplete: action.payload
      };
    case SET_ORDER_COMPLETE_ACTION:
      return {
        ...state,
        orderCompleteAction: action.payload
      };
    case SET_POST_SCAN_PIPELINE:
      return {
        ...state,
        postScanPipeline: {
          ...state.postScanPipeline,
          ...action.payload
        }
      };
    case SET_POST_SCAN_TRACKING_INPUT:
      return {
        ...state,
        postScanPipeline: {
          ...state.postScanPipeline,
          trackingInput: action.payload
        }
      };
    case SET_ALIAS_STATE:
      return {
        ...state,
        alias: {
          ...state.alias,
          ...action.payload
        }
      };
    case SET_SHIPMENT_STATE:
      return {
        ...state,
        shipment: {
          ...state.shipment,
          ...action.payload
        }
      };
    case SET_NAVIGATION_SOURCE:
      return {
        ...state,
        navigationSource: action.payload
      };
    case SET_WMS_ZERO_PENDING:
      return {
        ...state,
        wmsZeroPending: action.payload
      };
    case SET_BOX_CHAMP_STATE:
      return {
        ...state,
        boxChamp: {
          ...state.boxChamp,
          ...action.payload
        }
      };
    case RESET_BOX_CHAMP_STATE:
      return {
        ...state,
        boxChamp: { ...initialBoxChampState }
      };
    default:
      return state;
  }
}
